// Recover from stale code-split chunks after a deploy.
//
// The app lazy-loads route screens (App.tsx `lazyNamed`). Each build hashes its
// chunks (`/assets/director-<hash>.js`); a deploy rotates those hashes. A client
// that loaded the app *before* a deploy still holds the old hashes, so when the
// user opens a not-yet-loaded tab it requests a chunk that no longer exists.
//
// On Cloudflare Pages a missing `/assets/*.js` does not 404 — the SPA fallback
// returns `index.html` (HTTP 200, text/html). The browser refuses to run HTML as
// a module, so the dynamic `import()` rejects, `React.lazy` throws during render,
// and the RouteErrorBoundary shows "Не удалось открыть экран".
//
// Fix: when a chunk import fails, reload once to fetch the fresh index.html (and
// its new chunk hashes). A short sessionStorage debounce stops a genuinely broken
// chunk (not merely stale) from looping the page — after one reload we let the
// error surface to the boundary.

const RELOAD_KEY = 'pt:chunkReloadAt'
const DEBOUNCE_MS = 10_000

/**
 * Pure decision: given the current time and a storage, should we reload now?
 * Records the attempt timestamp when it returns true. Extracted from the
 * side-effecting path so the debounce is unit-testable without a DOM.
 */
export function shouldReloadForStaleChunk(
  now: number,
  storage: Pick<Storage, 'getItem' | 'setItem'>,
): boolean {
  const raw = storage.getItem(RELOAD_KEY)
  const last = raw == null ? null : Number(raw)
  if (last == null || !Number.isFinite(last) || now - last > DEBOUNCE_MS) {
    storage.setItem(RELOAD_KEY, String(now))
    return true
  }
  return false
}

/**
 * Reload the page once to recover from a stale chunk. Returns true if a reload
 * was triggered (caller should hold the render so no error flashes), false if we
 * already reloaded recently (caller should rethrow so the boundary can show).
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    if (shouldReloadForStaleChunk(Date.now(), sessionStorage)) {
      window.location.reload()
      return true
    }
  } catch {
    // sessionStorage unavailable (private mode / blocked) — fall through and let
    // the caller rethrow rather than risk a reload loop with no debounce.
  }
  return false
}
